import type { ReactNode } from 'react';
import { useIsAdmin } from '@/hooks/use-scopes';
import { cn } from '@/lib/cn';

/**
 * Diagnostics the team should not see.
 *
 * Revision rounds, raw audit fields, internal state names: useful to the
 * owner when something looks wrong, noise to an editor. Wrapped here they
 * render only for owner and manager, outlined in red so it is obvious the
 * block is admin-only and not part of the normal screen. For everyone else
 * the block does not exist.
 */
export default function AdminOnly({ children, inline, label = 'admin', className }: {
  children: ReactNode; inline?: boolean; label?: string; className?: string;
}) {
  const isAdmin = useIsAdmin();
  if (!isAdmin) return null;
  const Tag = inline ? 'span' : 'div';
  return (
    <Tag
      data-admin-only
      className={cn(
        'relative border border-dashed border-red-500/60 rounded-md',
        inline ? 'inline-flex items-center gap-1.5 px-1.5 py-0.5 align-middle' : 'p-2 pt-4 mt-1',
        className,
      )}
    >
      <span className={cn(
        'font-mono text-[9px] uppercase tracking-wider text-red-400 select-none',
        inline ? '' : 'absolute -top-2 left-2 bg-bg-card px-1',
      )}>{label}</span>
      {children}
    </Tag>
  );
}
